/**
 * LedgerAutocomplete - Dropdown with grouped ledger selection
 * Used in editable tables for bank statement import
 */

import React, { useState, useMemo } from 'react';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { SelectOption } from './types';

interface LedgerAutocompleteProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}

export const LedgerAutocomplete: React.FC<LedgerAutocompleteProps> = ({
  value,
  options,
  onChange
}) => {
  const [search, setSearch] = useState('');

  // Group options by ledger group
  const groupedOptions = useMemo(() => {
    const groups: Record<string, SelectOption[]> = {};
    options.forEach(opt => {
      const group = opt.group || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(opt);
    });
    return groups;
  }, [options]);

  // Filter by search
  const filteredGroups = useMemo(() => {
    if (!search) return groupedOptions;

    const filtered: Record<string, SelectOption[]> = {};
    Object.entries(groupedOptions).forEach(([group, opts]) => {
      const matching = opts.filter(o =>
        o.label.toLowerCase().includes(search.toLowerCase())
      );
      if (matching.length > 0) filtered[group] = matching;
    });
    return filtered;
  }, [groupedOptions, search]);

  return (
    <Select.Root value={value} onValueChange={onChange}>
      <Select.Trigger className="w-full text-left px-2 py-1 border rounded text-sm bg-background flex items-center justify-between hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring">
        <Select.Value placeholder="Select ledger..." />
        <Select.Icon>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content className="overflow-hidden bg-popover text-popover-foreground rounded-md border shadow-md z-50 w-64 max-h-96">
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Search ledger..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <Select.Viewport className="p-1 overflow-auto max-h-80">
            {Object.entries(filteredGroups).length === 0 ? (
              <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                No ledgers found
              </div>
            ) : (
              Object.entries(filteredGroups).map(([group, opts]) => (
                <Select.Group key={group}>
                  <Select.Label className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {group}
                  </Select.Label>
                  {opts.map(opt => (
                    <Select.Item
                      key={opt.value}
                      value={opt.value}
                      className={cn(
                        "relative flex items-center px-2 py-1.5 text-sm rounded-sm outline-none cursor-pointer",
                        "hover:bg-accent hover:text-accent-foreground",
                        "focus:bg-accent focus:text-accent-foreground",
                        "data-[state=checked]:bg-accent data-[state=checked]:text-accent-foreground"
                      )}
                    >
                      <Select.ItemText>{opt.label}</Select.ItemText>
                      <Select.ItemIndicator className="ml-auto">
                        <Check className="h-4 w-4" />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.Group>
              ))
            )}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};
